import React, {Dispatch, ReactNode, useState} from 'react';
import {useCatalogLayers} from '../../hooks/useCatalogLayers';
import {actionApiCall, WaitForQueryData} from '@mc/react-app-v4';
import {SidebarContent} from './SidebarContent';
import {CatalogLayerTreeList} from './CatalogLayerTreeList';
import {MobileCatalogLayerList} from './MobileCatalogLayerList';
import {CatalogItemVO, LayerVO, TopicVO} from '../../api';
import {UseQueryResult} from 'react-query';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {useTopicsMap} from '../../hooks/useTopicsMap';
import {Button, Icon, Label, Menu, MenuItem, Modal, Space} from '@mc/ui-v4';
import {mcClose, mcLayers, mcSettings, mcSortDesc} from '@mc/react-ui-icons';
import {useMapioContext} from '../../mapioContext';
import {ConfigurationModal} from '../modals/ConfigurationModal/ConfigurationModal';
import {DateFilterItemDefinition, FeatureFilter, ExternalType, LayerId} from '@mc/react-mapio';
import {useAppConfig} from '@mc/react-app-base';
import {DateFilterSelector} from './DateFilterSelector';
import {useIsMobile} from '../../hooks/useIsMobile';
import {BottomSheet} from '../mobile/BottomSheet';
import TopicSelectDialog from '../modals/TopicSelectDialog';
import {AddExternalLayer} from '../modals/AddExternalLayer/AddExternalLayer';
import {useMamPrirazenouAplikaci} from '../../hooks/useMamPrirazenouAplikaci';
import {LegendModal} from '../modals/LegendModal';
import {ApiCallWaitingModal} from '../modals/ApiCallWaitingModal';
import {preloadLegendData} from '../../helpers/LegendUtils';
import classNames from 'classnames';
import './CatalogLayersSidebar.scss';

type Props = {
    /** Callback volaný při změně aktivity některé z daných vrstev vybraného topicu / tématu. Klikunutí na checkbox vrstvy. */
    onChange: (activeLayers: LayerId[]) => void;
    /** Callback volaný při změně vybraného topicu / tématu. */
    onChangeTopic: (values: string) => void;
    /** Callback při kliknutí na tlačítko vrstvy. Tlačítko je vidět na konci řádky vrstvy po najití myši na danou řádku. */
    onClickLayerButton: (layerId: LayerId) => void;
    onClose: () => void;
    backgroundLayer: LayerId;
    setBackgroundLayer: Dispatch<React.SetStateAction<LayerId>>;
    foregroundLayer: LayerId;
    setForegroundLayer: Dispatch<React.SetStateAction<LayerId>>;
    activeLayers: LayerId[];
    setActiveLayers: Dispatch<React.SetStateAction<LayerId[]>>;
    dateFilterRange: FeatureFilter | null;
    setDateFilterRange: React.Dispatch<React.SetStateAction<FeatureFilter | null>>;
    dateFilterItems: DateFilterItemDefinition[];
    topicChangeEnabled?: boolean;
    showTopicSelector?: boolean;
};

export const CatalogLayersSidebar: React.FC<Props> = ({
    onChangeTopic,
    onClose,
    backgroundLayer,
    setBackgroundLayer,
    foregroundLayer,
    setForegroundLayer,
    activeLayers,
    setActiveLayers,
    dateFilterRange,
    setDateFilterRange,
    dateFilterItems,
    topicChangeEnabled,
    showTopicSelector,
    ...otherProps
}) => {
    // @ts-ignore
    const {serverPublic} = useAppConfig();
    const isMobile = useIsMobile();
    const [expandedLayerIdList, setExpandedLayerIdList] = useState<string[]>([]);
    const topicsMapQuery: UseQueryResult<TopicVO[]> = useTopicsMap();
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    const mapioContext = useMapioContext();
    const {externalLayers, externalLayersCatalog, topicId, mapRef, layerSettingsEnabled} = mapioContext.value;
    const catalogLayersQuery: UseQueryResult<CatalogItemVO[]> = useCatalogLayers(topicId);
    const mamPrirazenouAplikaciQuery = useMamPrirazenouAplikaci();

    const handleChangeTopic = (value: string): void => {
        if (topicId === value) return;
        onChangeTopic(value);
        const topic: TopicVO = topicsMapQuery.data.find((item: TopicVO) => item.id === value);
        setActiveLayers(topic?.selectedLayers ? topic?.selectedLayers : []);
        mapioContext.set({...mapioContext.value, topicId: value});
        mapioContext.setTopic(topic);
    };

    const handleLayerDelete = (layerId: LayerId) => {
        const layers = [...activeLayers];
        if (layers.indexOf(layerId) > -1) {
            setActiveLayers(layers.splice(layers.indexOf(layerId), 1));
        }
    };

    const openSettings = (): void => {
        Modal.show(({onClose}) => <ConfigurationModal onClose={onClose} onLayerDelete={handleLayerDelete} mapRef={mapRef} />);
    };

    const openTopicDialog = (): void => {
        Modal.show(({onClose}) => <TopicSelectDialog onClose={onClose} topics={topicsMapQuery.data} selectedTopicId={topicId} onSelect={handleChangeTopic} />);
    };

    const handleOpenLegend = async (): Promise<void> => {
        const {lang, context} = mapioContext.value;
        // Přednačteme všechna data legendy (atributy pro vyhledávání, konfiguraci vrstev i legendy WMS/WMTS vrstev)
        // jedním Promise.all, aby se legenda pod katalogem otevřela rovnou s daty (vč. atributů, podle kterých lze ve vrstvě hledat).
        const {searchableAttributes, layersConfig, layerLegends} = await Modal.showWaiting(
            actionApiCall(async () => await preloadLegendData(activeLayers as LayerId[], lang, context, layersConfigQuery.data)),
            () => <ApiCallWaitingModal onClose={undefined} />,
        );

        Modal.show(({onClose}) => (
            <LegendModal
                onClose={onClose}
                layerIds={activeLayers}
                searchableAttributes={searchableAttributes}
                layersConfig={layersConfig}
                layerLegends={layerLegends}
                formattedLegend={topicsMapQuery.data?.find((item: TopicVO) => item.id === topicId)?.formatovanaLegenda}
            />
        ));
    };

    // Tlačítko správy témat/vrstev (ozubené kolečko). Sdílené pro desktop (SidebarContent)
    // i mobil (BottomSheet) – dříve bylo jen v desktop větvi, takže na mobilu chybělo.
    const settingsButton =
        !serverPublic && mamPrirazenouAplikaciQuery?.data && layerSettingsEnabled ? (
            <Button
                title={'Správa témat/vrstev'}
                disabled={!catalogLayersQuery.isFetched || !topicsMapQuery.isFetched}
                icon={<Icon icon={mcSettings} onClick={onClose} size={'lg'} />}
                onClick={openSettings}
                style={'link'}
            />
        ) : null;

    const renderCatalogContent = () => (
        <div className={'pb-2 px-2'}>
            <WaitForQueryData queries={[topicsMapQuery, layersConfigQuery, catalogLayersQuery]}>
                {() => (
                    <>
                        {Boolean(topicsMapQuery.data?.length) &&
                            showTopicSelector &&
                            (isMobile ? (
                                <Button
                                    className={'w-100 mb-2 px-2'}
                                    style={'primary'}
                                    disabled={topicChangeEnabled === false}
                                    iconRight={<Icon icon={mcSortDesc} />}
                                    onClick={() => openTopicDialog()}
                                >
                                    {topicsMapQuery.data.find((item: TopicVO): boolean => item.id === topicId)?.label || 'Žádné téma'}
                                </Button>
                            ) : (
                                <Button
                                    className={'w-100 mb-2 px-2'}
                                    menu={
                                        <Menu className="select-scroll">
                                            {topicsMapQuery.data.map(
                                                (item: TopicVO): ReactNode => (
                                                    <MenuItem
                                                        active={item.id === topicId}
                                                        key={item.id}
                                                        onClick={() => handleChangeTopic(item.id)}
                                                        text={item.label}
                                                    />
                                                ),
                                            )}
                                        </Menu>
                                    }
                                    popupProps={{position: 'bottom center'}}
                                    style={'primary'}
                                    disabled={topicChangeEnabled === false}
                                >
                                    {topicsMapQuery.data.find((item: TopicVO): boolean => item.id === topicId)?.label || 'Žádné téma'}
                                </Button>
                            ))}
                        <Space>
                            {!serverPublic && topicsMapQuery.data.find((item: TopicVO): boolean => item.id === topicId)?.showDateFilter && (
                                <DateFilterSelector
                                    mapRef={mapRef}
                                    dateFilterItems={dateFilterItems}
                                    dateFilterRange={dateFilterRange}
                                    setDateFilterRange={setDateFilterRange}
                                />
                            )}
                            {!serverPublic && (
                                <Button
                                    menu={
                                        <Menu className="select-scroll">
                                            <MenuItem
                                                key={`vrstva-ze-souboru`}
                                                onClick={() => {
                                                    // Zavřeme katalog, ať se modální dialog nezobrazí za ním
                                                    // (bottom-sheet i postranní karta jinak překrývaly dialog).
                                                    onClose();
                                                    Modal.show(({onClose}) => (
                                                        <AddExternalLayer type={ExternalType.FILE} title={'Připojit vrstvy ze souboru'} onClose={onClose} />
                                                    ));
                                                }}
                                                text={'Ze souboru'}
                                            />
                                            <MenuItem
                                                key={`vrstva-wms`}
                                                onClick={() => {
                                                    onClose();
                                                    Modal.show(({onClose}) => (
                                                        <AddExternalLayer type={ExternalType.WMS} title={'Připojit WMS vrstvu'} onClose={onClose} />
                                                    ));
                                                }}
                                                text={'WMS'}
                                            />
                                        </Menu>
                                    }
                                    popupProps={{position: 'bottom right'}}
                                >
                                    Připojit vrstvy
                                </Button>
                            )}
                        </Space>
                        <Label>Vrstvy</Label>
                        {serverPublic && isMobile ? (
                            <MobileCatalogLayerList
                                activeLayers={activeLayers}
                                data={catalogLayersQuery.data}
                                layersConfig={layersConfigQuery.data}
                                onChange={otherProps.onChange}
                                setActiveLayers={setActiveLayers}
                            />
                        ) : (
                            <CatalogLayerTreeList
                                activeLayers={activeLayers}
                                data={catalogLayersQuery.data}
                                expandedLayerIdList={expandedLayerIdList}
                                layersConfig={layersConfigQuery.data}
                                level={0}
                                setActiveLayers={setActiveLayers}
                                setExpandedLayerIdList={setExpandedLayerIdList}
                                {...otherProps}
                            />
                        )}
                        {externalLayers.length > 0 && (
                            <>
                                <Label>Externí vrstvy</Label>
                                <CatalogLayerTreeList
                                    activeLayers={activeLayers}
                                    data={externalLayersCatalog}
                                    expandedLayerIdList={expandedLayerIdList}
                                    layersConfig={layersConfigQuery.data}
                                    level={0}
                                    setActiveLayers={setActiveLayers}
                                    setExpandedLayerIdList={setExpandedLayerIdList}
                                    {...otherProps}
                                />
                            </>
                        )}
                    </>
                )}
            </WaitForQueryData>
        </div>
    );

    return (
        <>
            {isMobile ? (
                <BottomSheet
                    open
                    onClose={onClose}
                    snapPoints={[14, 50, 100]}
                    defaultSnapPointIndex={0}
                    title={
                        <>
                            <Icon className={'mr-1'} icon={mcLayers} />
                            {'Zobrazení'}
                        </>
                    }
                    extra={settingsButton}
                >
                    {renderCatalogContent()}
                </BottomSheet>
            ) : (
                <SidebarContent icon={mcLayers} title={'Zobrazení'} className={'catalog-layers-sidebar'} onClose={onClose} extra={settingsButton}>
                    <div className={classNames({'scrollable-content': !isMobile}, 'pb-1')}>{renderCatalogContent()}</div>
                    <div className="d-flex justify-content-end pr-2 py-1">
                        <Button onClick={handleOpenLegend} style={'link'}>
                            Legenda
                        </Button>
                    </div>
                </SidebarContent>
            )}
        </>
    );
};
